<template>
  <ColumnAdjuster
    ref="adjuster"
    :propColumnSet="propColumnSet"
    :isOpen="isOpen"
    :showReset="showReset"
    :removeFromTabIndex="removeFromTabIndex"
    :appendElementId="appendElementId"
    elementId="tabbed-column-adjuster"
    :containerStyle="containerStyle"
    :color="color"
    :dialogColor="dialogColor"
    :desktopIconSize="desktopIconSize"
    :showTooltip="showTooltip"
    :mobileCompatible="mobileCompatible"
    @new-columns-selected="columnSet => $emit('new-columns-selected', columnSet)"
    @reset="$emit('reset')"
    @closed="closed">
    <template #default="{ columnSet }">
      <v-tabs v-model="currentTab" show-arrows>
        <v-tab v-for="(tab, index) in validTabs" :key="tab">
          <span>{{$t(tab)}}</span>

          <Icon
            class="ml-3"
            top
            :icon="`mdi-checkbox-multiple-${allVisible(columnSet.getColumnsByGroup(tab)) ? 'marked' : 'blank-outline'}`"
            :iconColor="allVisible(columnSet.getColumnsByGroup(tab)) ? 'success' : 'rgba(0,0,0,0.3)'"
            :tooltipText="$t(allVisible(columnSet.getColumnsByGroup(tab)) ? 'tabSelectNoneCheckboxTooltip' : 'tabSelectAllCheckboxTooltip', { tabType: $t(tab) })"
            tooltipColor="primary"
            :small="false"
            @icon-clicked="toggleTab(columnSet, tab, index)"/>
        </v-tab>

        <v-tab-item v-for="tab in validTabs" :key="tab">
          <ColumnToggleGrid
            class="mt-1"
            :columns="columnSet.getColumnsByGroup(tab)"
            @toggle="column => columnSet.toggleColumn(column.id)"/>
        </v-tab-item>
      </v-tabs>
    </template>
  </ColumnAdjuster>
</template>

<script>
import { ColumnSet } from '@/model/DataTableColumn.js'
import { getMenuContainerStyle } from '@/utils/componentHelpers'

export default {
  name: 'TabbedColumnAdjuster',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    ColumnToggleGrid: () => import('@/components/core/ColumnToggleGrid.vue')
  },

  props: {
    propColumnSet: ColumnSet,
    dynamicSize: {
      type: Boolean,
      required: false,
      default: false
    },
    propTabs: {
      type: Array,
      default: () => []
    },
    isOpen: {
      type: Boolean,
      required: false,
      default: false
    },
    showReset: {
      type: Boolean,
      default: false
    },
    removeFromTabIndex: {
      type: Boolean,
      default: false
    },
    appendElementId: {
      type: String,
      required: false,
      default: undefined
    },
    color: {
      type: String,
      required: false,
      default: 'primary'
    },
    dialogColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    desktopIconSize: {
      type: String,
      required: false,
      default: '1.5em'
    },
    showTooltip: {
      type: Boolean,
      required: false,
      default: true
    },
    mobileCompatible: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    currentTab: 0,
    tabBarWidth: null
  }),

  computed: {
    validTabs () {
      return this.propTabs.filter(tab => this.propColumnSet.getColumnsByGroup(tab).length > 0)
    },

    containerStyle () {
      const fitWidth = this.tabBarWidth
        ? `min(max(600px, ${this.tabBarWidth}px), 95vw)`
        : 'min(600px, 100vw)'
      const defaultWidthObject = { width: fitWidth }

      const { width, height } = this.$vuetify.breakpoint
      if (!this.dynamicSize || (width > 550 && height > 800)) return defaultWidthObject

      return getMenuContainerStyle(width, height)
    }
  },

  watch: {
    validTabs: {
      handler () {
        this.computeTabBarWidth()
      },
      immediate: true
    }
  },

  methods: {
    // Measured before render so v-tabs mounts wide enough for every translated label instead of paginating.
    computeTabBarWidth () {
      if (!this.validTabs.length) return

      const measurer = document.createElement('div')
      measurer.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;left:-9999px;top:0'

      this.validTabs.forEach(tab => {
        const tabEl = document.createElement('span')
        tabEl.style.cssText = 'display:inline-flex;align-items:center;box-sizing:border-box;' +
          'font-size:0.875rem;font-weight:500;letter-spacing:0.0892857143em;text-transform:uppercase;' +
          'padding:0 16px;min-width:90px'

        const label = document.createElement('span')
        label.textContent = this.$t(tab)
        tabEl.appendChild(label)

        const icon = document.createElement('span')
        icon.style.cssText = 'display:inline-block;width:24px;margin-left:12px;margin-right:4px'
        tabEl.appendChild(icon)

        measurer.appendChild(tabEl)
      })

      document.body.appendChild(measurer)
      const tabsWidth = Array.from(measurer.children).reduce((sum, el) => sum + el.offsetWidth, 0)
      document.body.removeChild(measurer)

      const containerPadding = 24
      this.tabBarWidth = Math.ceil(tabsWidth + containerPadding)
    },

    allVisible (columns) {
      return columns.every(column => column.visible)
    },

    toggleTab (columnSet, tab, tabIndex) {
      this.currentTab = tabIndex
      columnSet.toggleColumns(columnSet.getColumnsByGroup(tab))
    },

    closed () {
      this.currentTab = 0
      this.$emit('closed')
    },

    openMenu () {
      this.$refs.adjuster.openMenu()
    }
  }
}
</script>
